import {Provider} from 'mobx-react';
import {useState} from 'react';

import {BrowseCourse} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {ClickEvent, Tracker} from '@udemy/event-tracking';
import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button} from '@udemy/react-core-components';
import {
    HorizontalBundleUnit,
    DiscoveryUnitsContainerStore,
    CourseUnit,
} from '@udemy/react-discovery-units';
import {LinkGroup} from '@udemy/react-structure-components';
import {useUDData} from '@udemy/ud-data';

import {TopicPageFeatureProps} from 'src/features/topic';
import {TopicRelatedDataItem} from 'src/pages/topic/lib/get-related-data';

import {SectionLayoutWrapper} from '../section-layout-wrapper/section-layout-wrapper.react-component';
import {CategoryNumberIcon} from '../topic-category/topic-category-menu.react-component';
import styles from './hottest-topic-section.module.less';

export const HottestTopicSection: React.FC<{
    section: {
        topic: TopicPageFeatureProps;
        genericTopicBundleCourses?: BrowseCourse[];
        popularTopics?: DiscoveryUnit;
        relatedCategoriesAndSubcategories?: TopicRelatedDataItem[];
    };
    sectionNumber: number;
}> = ({section, sectionNumber}) => {
    const {topic, genericTopicBundleCourses, relatedCategoriesAndSubcategories} = section;
    const udData = useUDData();
    const {gettext, interpolate} = useI18n();
    const sectionSlug = topic.topic.url.split('/').pop();
    const isSectionAI = sectionSlug === 'artificial-intelligence';
    const description = isSectionAI
        ? gettext(
              'Future-proof your skills with AI courses that teach you how to integrate AI in your day-to-day.',
          )
        : topic.metaProps.seoDescription.split('.')[0] + '.';
    const popularTopics = section.popularTopics
        ? Object.assign({}, section.popularTopics, {
              raw_title: gettext('Top {} courses'),
          })
        : null;

    const topicName = topic.topic.name;

    const [discoveryUnitsStore] = useState(
        () =>
            new DiscoveryUnitsContainerStore(
                {
                    pageType: 'black-friday-cyber-monday-sales',
                    units: popularTopics ? [popularTopics] : [],
                },
                udData,
            ),
    );

    const trackAllTopicsClick = (componentName: string) => {
        Tracker.publishEvent(
            new ClickEvent({
                componentName: componentName,
            }),
        );
    };

    function renderRelatedTopicsSubSection() {
        if (!relatedCategoriesAndSubcategories || relatedCategoriesAndSubcategories.length === 0) {
            return null;
        }

        const template = gettext('Top related <a class="topicLink">%(topicName)s</a> skills');
        const htmlWithInterpolatedText = interpolate(template, {topicName: topicName}, true);

        return (
            <>
                <div className={styles['related-topics-sub-section']}>
                    <h3 className={styles['related-topics-sub-section-title']}>
                        <LocalizedHtml
                            html={htmlWithInterpolatedText}
                            interpolate={{
                                topicLink: (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a
                                        href={topic.topic.url}
                                        aria-label={topicName}
                                        className="ud-link-underline"
                                        onClick={() =>
                                            trackAllTopicsClick('seeAllTopicsSectionTitle')
                                        }
                                    />
                                ),
                            }}
                        />
                    </h3>
                    <LinkGroup ariaLabel={gettext('Related topics')}>
                        {relatedCategoriesAndSubcategories.map((category) => (
                            <LinkGroup.Item href={category.url} key={category.id}>
                                {category.title}
                            </LinkGroup.Item>
                        ))}
                    </LinkGroup>
                </div>
            </>
        );
    }

    const renderHottestTopicSectionTitle = () => {
        return (
            <>
                <CategoryNumberIcon
                    number={sectionNumber}
                    className={styles['hottest-topic-section-icon-desktop']}
                />
                <div className={styles['hottest-topic-section-title-content']}>
                    <h2 className={styles['hottest-topic-section-title-text']}>{topicName}</h2>
                    <p className={styles['hottest-topic-section-description']}>{description}</p>
                </div>
                <p className={styles['hottest-topic-section-description-mobile']}>{description}</p>
            </>
        );
    };

    return (
        <SectionLayoutWrapper title={topicName} sectionNumber={sectionNumber}>
            <section className={styles['hottest-topic-section']} id={sectionSlug}>
                <div className={styles['hottest-topic-section-title']}>
                    {renderHottestTopicSectionTitle()}
                </div>
                <div
                    className={styles['hottest-topic-section-bundle-container']}
                    data-item-index={sectionNumber}
                >
                    <HorizontalBundleUnit
                        contents={genericTopicBundleCourses}
                        title={interpolate(
                            gettext(
                                'Your Black Friday Upgrade: Best-Selling %(topicName)s Courses in One Bundle!',
                            ),
                            {topicName: topicName},
                            true,
                        )}
                        subtitle={gettext(
                            'Get everything you need to advance your skills and reach your goals in one convenient bundle.',
                        )}
                        valueProps={[
                            gettext('Top-rated courses'),
                            gettext('Popular with learners just like you'),
                            gettext('Guidance from real-world experts'),
                        ]}
                    />
                </div>

                {popularTopics && (
                    <Provider discoveryUnitsStore={discoveryUnitsStore}>
                        <CourseUnit
                            unit={popularTopics}
                            showTitle={true}
                            showQuickViewBox={true}
                            layout="singlerow"
                            fullWidth={true}
                            showPager={true}
                            gridCols={4}
                            carouselProps={{
                                ariaLabel: interpolate(
                                    gettext('Top %(topicName)s courses'),
                                    {topicName},
                                    true,
                                ),
                                ariaRoleDescription: gettext('carousel'),
                            }}
                        />
                        <Button
                            componentClass="a"
                            udStyle="ghost"
                            href={topic.topic.url}
                            onClick={() => trackAllTopicsClick('seeAllTopicsButton')}
                            target="_self"
                            size="medium"
                            className={styles['view-all-topic-courses-link']}
                        >
                            {interpolate(
                                gettext('All %(topicName)s courses'),
                                {topicName: topicName},
                                true,
                            )}

                            <Icon
                                label={false}
                                glyph="arrow-right"
                                className="ud-icon-bi-directional"
                            />
                        </Button>
                    </Provider>
                )}

                {renderRelatedTopicsSubSection()}
            </section>
        </SectionLayoutWrapper>
    );
};
